import React, { useMemo } from 'react';
import { Bar } from '@nivo/bar';
import { useReportConfiguration } from 'components/reporting/sidebar/ConfiguredReport';
import {
  formatBarChartData,
  getColor,
  generateDefaultConfiguration,
  getDisplayValuesForSeries,
  getDisplayValueForDatum,
  openDrawerForDrillIn,
  onIdeaPortalSettingsPage,
} from '../helpers';
import AxesReset from '../custom_layers/AxesReset';
import { useChartScales } from './ChartScales';
import styles from './Chart.module.css';
import classNames from 'classnames';

const DEFAULT_FONT_SIZE = 12;

const generateBarChartTooltip = (datum, meta, percentStacking) => {
  const {
    id,
    data: { row_label },
  } = datum;
  // Compile the tooltip with the following formats:
  // <seriesDisplayLabel> - <row_label>: <displayValue> (when all values present)
  // <seriesDisplayLabel>: <displayValue> or <row_label>: <displayValue> (when missing values)
  const seriesDisplayLabel = getDisplayValuesForSeries(meta, id);
  const displayValue = getDisplayValueForDatum(datum);
  var displayString = '';
  if (seriesDisplayLabel) displayString += seriesDisplayLabel;
  if (row_label) {
    displayString.length > 0
      ? (displayString += ' - ' + row_label)
      : (displayString += row_label);
  }
  if (displayValue) {
    displayString.length > 0
      ? (displayString += ': ' + displayValue)
      : (displayString += displayValue);
  }

  if (percentStacking) {
    displayString = `${displayString}%`;
  }

  return <div role='tooltip'>{displayString}</div>;
};

/**
 * @typedef BarChartProps
 * @prop {*=} data
 * @prop {*=} meta
 * @prop {number} width
 * @prop {number} height
 * @prop {boolean} publishing
 */
/**
 * @type {React.FC<BarChartProps>}
 */
function BarChart({ data, meta, width, height, publishing }) {
  const { get } = useReportConfiguration();
  const { margins } = useChartScales();

  const percentStacking = get('percentStack') && get('groupMode') !== 'grouped';
  const orientation = get('orientation');
  const reversed = get(`reverse_${get('chartType')}`);
  const dataLabelFormat = Object.assign({}, get('dataLabelFormat'));
  let calculatedFontSize = parseInt(
    dataLabelFormat?.fontSize?.replace('px', ''),
    10
  );

  if (Number.isNaN(calculatedFontSize)) calculatedFontSize = DEFAULT_FONT_SIZE;

  const memoizedData = useMemo(() => {
    const chartData = formatBarChartData({
      data: JSON.parse(JSON.stringify(data)),
      meta,
      percentStacking,
      reversed,
    });
    return orientation === 'vertical' ? chartData : [...chartData].reverse();
  }, [data, meta, percentStacking, reversed, orientation]);

  const barChartProps = generateDefaultConfiguration({
    configuration: get(),
    meta,
    width,
  });
  barChartProps.theme.labels = {
    text: dataLabelFormat,
  };

  const drillInEnabled = meta.drill_in?.enabled && !onIdeaPortalSettingsPage();
  function handleClick(node, event) {
    if (drillInEnabled) {
      openDrawerForDrillIn(node.data.column_index, node.id, meta, event.target);
    }
  }

  const classes = classNames(styles.BarChartContainer, {
    [styles.clickable]: drillInEnabled,
  });

  return (
    <div className={classes}>
      <Bar
        {...barChartProps}
        margin={margins}
        width={width}
        height={height}
        data={memoizedData}
        layout={get('orientation')}
        groupMode={get('groupMode')}
        colors={bar => getColor(get(), meta, bar.id)}
        label={datum => {
          return getDisplayValueForDatum(datum);
        }}
        labelFormat={label => {
          if (!get('showLabels')) return null;
          return percentStacking ? `${label}%` : label;
        }}
        labelTextColor={dataLabelFormat.color}
        tooltip={datum => generateBarChartTooltip(datum, meta, percentStacking)}
        layers={['grid', 'bars', AxesReset, 'markers']}
        padding={1 - get('barSize') / 100}
        labelSkipWidth={calculatedFontSize + 15}
        labelSkipHeight={15}
        onClick={handleClick}
      />
    </div>
  );
}

export default BarChart;
